// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// convexMirror — write-path dual-run for the Convex experiment (#191).
//
// Lets the existing modelTS mutationFns route/mirror writes to Convex based on
// the user's backendMode, without rewriting them:
//
//   mongoOnly  → skipMongoWrite()=false, mirror is a no-op. Today's behavior.
//   convexOnly → skipMongoWrite()=true (caller skips the Lambda), mirror writes
//                to Convex and returns a Mongo-shaped ack.
//   both       → caller still does the Lambda write; mirror ALSO writes Convex
//                and logs a parity comparison (Axiom + Convex table).
//
// The Convex client has a plain .mutation(ref, args) for use outside React.
// Everything is best-effort + guarded so a Convex hiccup never breaks a Mongo
// write for "both" users.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { convexClient } from "../convex/convexClient";
import { refs } from "../convex/apiRefs";
import {
  CONVEX_SOURCE,
  usesConvexWrites,
  comparesBackends,
} from "./backendConfig";
import { createWideEvent } from "./wideEventLogger";

// In convexOnly mode the caller should NOT hit the Lambda/Mongo at all.
export function skipMongoWrite(mode) {
  return mode === "convexOnly";
}

// Did a write ack indicate success? Handles both update acks
// ({matchedCount/modifiedCount}) and insert acks ({acknowledged/insertedId/upsertedId}).
export function ackSucceeded(ack) {
  if (!ack || typeof ack !== "object") return false;
  if (typeof ack.matchedCount === "number") return ack.matchedCount > 0 || ack.modifiedCount > 0;
  if (ack.acknowledged) return true;
  if (ack.insertedId || ack.upsertedId) return true;
  if (typeof ack.upsertedCount === "number") return ack.upsertedCount > 0 || ack.acknowledged;
  return false;
}

/**
 * Route/mirror one mutation to Convex.
 * @param {object} o
 * @param {string} o.method      key in apiRefs (e.g. "setScheduleById")
 * @param {object} o.convexArgs  args for the Convex mutation (must include source)
 * @param {string} o.mode        resolved backendMode
 * @param {string} [o.username]  for logging
 * @param {string} [o.label]     comparison label (doc id, etc.)
 * @param {object} [o.mongoAck]  the Lambda's ack (for "both" comparison)
 * @returns {Promise<object|null>} the Convex ack (or null when not applicable)
 */
export async function mirrorMutationToConvex({
  method,
  convexArgs,
  mode,
  username,
  label,
  mongoAck,
}) {
  if (!usesConvexWrites(mode) || !convexClient || !refs[method]) return null;
  try {
    const convexAck = await convexClient.mutation(refs[method], convexArgs);

    if (comparesBackends(mode) && mongoAck !== undefined) {
      const mOk = ackSucceeded(mongoAck);
      const cOk = ackSucceeded(convexAck);
      const match = mOk === cOk;
      const diffSummary = match
        ? ""
        : `mongoOk=${mOk} convexOk=${cOk}`;

      const ev = createWideEvent("convex-parity");
      ev.set("_experiment", "#191");
      ev.set("kind", "mutation");
      ev.set("method", method);
      ev.set("label", label);
      ev.set("source", CONVEX_SOURCE);
      ev.set("match", match);
      ev.set("mongoAck", JSON.stringify(mongoAck || null).slice(0, 300));
      ev.set("convexAck", JSON.stringify(convexAck || null).slice(0, 300));
      ev.set("user", username);
      ev.send && ev.send();

      convexClient
        .mutation(refs.recordComparison, {
          source: CONVEX_SOURCE,
          kind: "mutation",
          method,
          label: label || "",
          match,
          diffSummary,
          user: username,
          createdMs: Date.now(),
        })
        .catch(() => {});
    }
    return convexAck;
  } catch (err) {
    // Never let a Convex failure break the (already-completed) Mongo write.
    // eslint-disable-next-line no-console
    console.warn("convexMirror: mutation failed (non-fatal):", method, err?.message);
    return null;
  }
}
